/**
 * Composant - DepositFormStep
 *
 * Billetage d'un versement : déposant (titulaire ou tiers identifié), coupures remises, notes, validation.
 * Même présentation que l'étape « Montant » du retrait (styles withdrawal/etapes.css).
 */

import React, { useState } from 'react';
import { AlertTriangle, Banknote, CheckCircle, IdCard, Loader2, Lock, PiggyBank, RotateCcw, UserRound, Users } from 'lucide-react';
import { Account } from '../../../domain/entities/Account';
import { SelecteurCoupures } from '../common/SelecteurCoupures';
import { createEmptyDenominationMap, calculateTotal } from '../../../shared/constants/denominations';
import { formatMoney } from '../../../shared/utils/formatters';
import { montantEnLettres } from '../../../shared/utils/montantEnLettres';
import '../withdrawal/etapes.css';

/** Pièces acceptées pour identifier un déposant tiers (libellé repris tel quel sur le reçu) */
const TYPES_PIECE = ['CNI', 'Passeport', 'Permis de conduire', 'Carte consulaire', 'Carte de séjour', "Attestation d'identité"];

interface Tiers {
  nom: string;
  typePiece: string;
  numeroPiece: string;
  contact: string;
}

const TIERS_VIDE: Tiers = { nom: '', typePiece: 'CNI', numeroPiece: '', contact: '' };
const MESSAGE_TIERS_INCOMPLET = 'Complétez l’identité de la personne qui remet les espèces.';

/** Contrôles de saisie du déposant tiers ; renvoie un message par champ en erreur */
function controlerTiers(t: Tiers): Partial<Record<keyof Tiers, string>> {
  const erreurs: Partial<Record<keyof Tiers, string>> = {};
  const nom = t.nom.trim();
  if (nom.length < 3) erreurs.nom = 'Saisissez le nom et les prénoms du déposant.';
  else if (!/^\p{L}[\p{L} '.-]*$/u.test(nom)) erreurs.nom = 'Le nom ne doit contenir que des lettres.';
  const numero = t.numeroPiece.replace(/\s/g, '');
  if (numero.length < 5) erreurs.numeroPiece = 'Saisissez le numéro complet de la pièce.';
  else if (!/^[A-Za-z0-9/-]+$/.test(numero)) erreurs.numeroPiece = 'Lettres, chiffres, « - » et « / » uniquement.';
  const chiffres = t.contact.replace(/\D/g, '');
  if (chiffres.length < 8 || chiffres.length > 15) erreurs.contact = 'Saisissez un numéro de téléphone valide.';
  return erreurs;
}

interface DepositFormStepProps {
  account: Account;
  onSubmit: (data: {
    denomination: Map<number, number>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    depositorInfo: any;
    notes?: string;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
}

export function DepositFormStep({ account, onSubmit, onCancel, loading, error }: DepositFormStepProps) {
  const [bills, setBills] = useState<Map<number, number>>(createEmptyDenominationMap());
  const [notes, setNotes] = useState('');
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  const [estTiers, setEstTiers] = useState(false);
  const [tiers, setTiers] = useState<Tiers>(TIERS_VIDE);
  const [tiersVerifie, setTiersVerifie] = useState(false);

  const erreursTiers = estTiers ? controlerTiers(tiers) : {};
  const majTiers = (champ: keyof Tiers, valeur: string) => {
    setTiers((t) => ({ ...t, [champ]: valeur }));
    setErreurSaisie(null);
  };

  const total = calculateTotal(bills);
  const coTitulaires = account.getCoHolders();
  const nbBillets = Array.from(bills.values()).reduce((s, n) => s + n, 0);

  const handleBillChange = (denomination: number, count: number) => {
    const newBills = new Map(bills);
    newBills.set(denomination, count);
    setBills(newBills);
    setErreurSaisie(null);
  };

  /** Boutons − / + : mise à jour fonctionnelle, aucun clic perdu même très rapide */
  const handleBillDelta = (denomination: number, delta: number) => {
    setBills((prev) => {
      const next = new Map(prev);
      next.set(denomination, Math.max(0, (prev.get(denomination) ?? 0) + delta));
      return next;
    });
    setErreurSaisie(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading) return;

    if (estTiers && Object.keys(erreursTiers).length > 0) {
      setTiersVerifie(true);
      setErreurSaisie(MESSAGE_TIERS_INCOMPLET);
      document.getElementById('versement-deposant')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (total <= 0) {
      setErreurSaisie('Saisissez les coupures remises par le déposant.');
      return;
    }

    const depositorInfo = estTiers
      ? {
          isThirdParty: true,
          thirdParty: {
            name: tiers.nom.trim().replace(/\s+/g, ' '),
            idType: tiers.typePiece,
            idNumber: tiers.numeroPiece.replace(/\s/g, '').toUpperCase(),
            contact: tiers.contact.trim().replace(/\s+/g, ' '),
          },
        }
      : {
          isThirdParty: false,
          clientId: account.primaryHolder.id,
          clientName: account.primaryHolder.name,
        };

    try {
      await Promise.resolve(
        onSubmit({
          denomination: bills,
          depositorInfo,
          notes: notes || undefined,
        }),
      );
    } catch (err) {
      console.error('Erreur lors de la soumission du formulaire:', err);
    }
  };

  return (
    <div className="et et-large">
      <section className="et-carte">
        <header className="et-entete">
          <span className="ico">
            <PiggyBank />
          </span>
          <div>
            <h2>Billetage du versement</h2>
            <p>Identifiez le déposant, comptez les espèces avec lui et saisissez chaque coupure.</p>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="et-corps">
          {/* Compte crédité */}
          <div className="et-client">
            <span className="et-avatar">{initiales(account.primaryHolder.name)}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p className="nom">{account.primaryHolder.name}</p>
              <p className="sous">
                Compte <span className="et-mono">{account.accountNumber.value}</span>
              </p>
            </div>
            {coTitulaires.length > 0 ? (
              <span className="et-pastille gris">
                <Users /> Compte joint · {coTitulaires.length + 1} titulaires
              </span>
            ) : (
              <span className="et-pastille vert">
                <CheckCircle /> Compte vérifié
              </span>
            )}
          </div>

          {/* Déposant */}
          <div id="versement-deposant">
            <p className="et-section-titre">
              <span>
                <UserRound /> Qui remet les espèces ?
              </span>
            </p>
            <div className="et-rapides" role="radiogroup" aria-label="Déposant" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <button
                type="button"
                role="radio"
                aria-checked={!estTiers}
                className={`et-rapide ${!estTiers ? 'actif' : ''}`}
                onClick={() => {
                  setEstTiers(false);
                  setErreurSaisie(null);
                }}
                disabled={loading}
              >
                Le titulaire du compte
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={estTiers}
                className={`et-rapide ${estTiers ? 'actif' : ''}`}
                onClick={() => {
                  setEstTiers(true);
                  setErreurSaisie(null);
                }}
                disabled={loading}
              >
                Une autre personne
              </button>
            </div>

            {estTiers && (
              <div className="et-grille" style={{ marginTop: 14 }}>
                <div className="et-champ pleine">
                  <label htmlFor="tiers-nom">
                    Nom et prénoms <span className="req">*</span>
                  </label>
                  <input
                    id="tiers-nom"
                    className={`et-input ${tiersVerifie && erreursTiers.nom ? 'erreur' : ''}`}
                    value={tiers.nom}
                    onChange={(e) => majTiers('nom', e.target.value)}
                    maxLength={120}
                    autoComplete="off"
                    disabled={loading}
                  />
                  {tiersVerifie && erreursTiers.nom && <span className="et-erreur-champ">{erreursTiers.nom}</span>}
                </div>
                <div className="et-champ">
                  <label htmlFor="tiers-type">
                    Pièce d&apos;identité <span className="req">*</span>
                  </label>
                  <select
                    id="tiers-type"
                    className="et-input"
                    value={tiers.typePiece}
                    onChange={(e) => majTiers('typePiece', e.target.value)}
                    disabled={loading}
                  >
                    {TYPES_PIECE.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="et-champ">
                  <label htmlFor="tiers-numero">
                    Numéro de la pièce <span className="req">*</span>
                  </label>
                  <input
                    id="tiers-numero"
                    className={`et-input et-mono ${tiersVerifie && erreursTiers.numeroPiece ? 'erreur' : ''}`}
                    value={tiers.numeroPiece}
                    onChange={(e) => majTiers('numeroPiece', e.target.value.toUpperCase())}
                    maxLength={40}
                    autoComplete="off"
                    disabled={loading}
                  />
                  {tiersVerifie && erreursTiers.numeroPiece && <span className="et-erreur-champ">{erreursTiers.numeroPiece}</span>}
                </div>
                <div className="et-champ pleine">
                  <label htmlFor="tiers-contact">
                    Téléphone <span className="req">*</span>
                  </label>
                  <input
                    id="tiers-contact"
                    type="tel"
                    inputMode="tel"
                    className={`et-input ${tiersVerifie && erreursTiers.contact ? 'erreur' : ''}`}
                    value={tiers.contact}
                    onChange={(e) => majTiers('contact', e.target.value.replace(/[^\d+\s.-]/g, ''))}
                    maxLength={20}
                    autoComplete="off"
                    disabled={loading}
                  />
                  {tiersVerifie && erreursTiers.contact && <span className="et-erreur-champ">{erreursTiers.contact}</span>}
                </div>
                <div className="et-note pleine">
                  <IdCard />
                  <p>Contrôlez la pièce originale : la photo doit correspondre à la personne présente. Ces informations figurent sur le reçu.</p>
                </div>
              </div>
            )}
          </div>

          {/* Montant total */}
          <div className="et-montant" aria-live="polite">
            <p className="lib">Montant versé</p>
            <p className={`val ${total > 0 ? '' : 'vide'}`}>{formatMoney(total)}</p>
            <p className="lettres">
              {total > 0
                ? `${montantEnLettres(total)} · ${nbBillets} coupure${nbBillets > 1 ? 's' : ''}`
                : 'Saisissez les coupures ci-dessous'}
            </p>
          </div>

          {/* Coupures */}
          <div>
            <p className="et-section-titre">
              <span>
                <Banknote /> Coupures remises
              </span>
              {total > 0 && (
                <button type="button" className="et-btn lien" onClick={() => setBills(createEmptyDenominationMap())} disabled={loading}>
                  <RotateCcw /> Remettre à zéro
                </button>
              )}
            </p>
            <SelecteurCoupures coupures={bills} onChange={handleBillChange} onDelta={handleBillDelta} disabled={loading} />
          </div>

          {/* Notes */}
          <div className="et-champ">
            <label htmlFor="notes-versement">
              Notes <span className="opt">(facultatif)</span>
            </label>
            <textarea
              id="notes-versement"
              className="et-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Informations complémentaires"
              rows={2}
            />
          </div>

          {(erreurSaisie || error) && (
            <div className="et-note erreur" role="alert">
              <AlertTriangle />
              <p>
                <strong>
                  {erreurSaisie
                    ? erreurSaisie === MESSAGE_TIERS_INCOMPLET
                      ? 'Déposant incomplet'
                      : 'Billetage incomplet'
                    : 'Le versement n’a pas abouti'}
                </strong>
                {erreurSaisie ?? error}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="et-btns">
            <button type="button" onClick={onCancel} disabled={loading} className="et-btn secondaire">
              Annuler
            </button>
            <button type="submit" disabled={loading || total <= 0} className="et-btn principal large">
              {loading ? (
                <>
                  <Loader2 className="et-tourne" /> Enregistrement…
                </>
              ) : (
                <>
                  <CheckCircle />
                  {total > 0 ? `Valider le versement de ${formatMoney(total)}` : 'Valider le versement'}
                </>
              )}
            </button>
          </div>
        </form>

        <p className="et-pied">
          <Lock />
          Le compte est crédité au CBS dès la validation.
        </p>
      </section>
    </div>
  );
}

function initiales(nom?: string) {
  return (
    (nom ?? '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((m) => m[0]?.toUpperCase())
      .join('') || '?'
  );
}
